/**
 * Shared formatting/labelling helpers for the Servers fleet surfaces
 * (`fleet-inventory.html` / `server-detail.html` / `logs-view.html` /
 * `enrolment-flow.html`, Slice A3). Kept as plain functions (no Vue/Pinia
 * dependency) so every `Servers/*.vue` component can import the same source
 * of truth rather than re-deriving labels/thresholds independently.
 *
 * `agent_status` and `health` are read as two INDEPENDENT fields throughout
 * this module — never derived from one another. See
 * `app\module\servers\Entity\Server`'s docblock (binding design rule) and
 * `design/components/agent-status-badge.html`.
 */

// The taxonomy terms actually registered by
// `app\module\servers\Hook\RegisterServersTaxonomiesHook` — term KEYS, not
// the aspirational labels the mockups show (those mockups pre-date the real
// taxonomy definitions). `Server::taxonomyValue()`/`taxonomyValues()` return
// term keys only (no label lookup in that API), so this is the client-side
// label map for them.
const TAXONOMY_LABELS = {
  server_role: {
    web: 'Web',
    db: 'Database',
    queue: 'Queue',
    worker: 'Worker',
    build: 'Build',
    'package-registry': 'Package registry',
    dev: 'Dev',
    production: 'Production',
  },
  provider: {
    digitalocean: 'DigitalOcean',
    aws: 'AWS',
    katapult: 'Katapult',
    'bare-metal': 'Bare metal',
    'docker-host': 'Docker host',
  },
  environment: {
    production: 'Production',
    staging: 'Staging',
    dev: 'Dev',
    sandbox: 'Sandbox',
  },
};

/**
 * `Server::toApiArray()` calls `Entity::getData()`, which returns raw
 * column values rather than routing through `Server::casts()` — so `ips`
 * sometimes arrives as its raw JSON-encoded string (`"[]"` / `"[{...}]"`)
 * instead of a decoded array, depending on whether the row was reloaded
 * since the cast last ran. Defensive parse so a `v-for` over this never
 * iterates a string's characters.
 */
export function parseIps(server) {
  const raw = server?.ips;
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw !== '') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function titleCase(key) {
  return String(key || '')
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** @param {string} taxonomyKey 'server_role' | 'provider' | 'environment' */
export function taxonomyLabel(taxonomyKey, termKey) {
  if (!termKey) {
    return null;
  }
  return (TAXONOMY_LABELS[taxonomyKey] && TAXONOMY_LABELS[taxonomyKey][termKey]) || titleCase(termKey);
}

const AGENT_STATUS_META = {
  healthy: { label: 'Healthy', icon: 'check' },
  stale: { label: 'Stale', icon: 'clock' },
  unmanaged: { label: 'Unmanaged', icon: 'cross' },
  enrolling: { label: 'Enrolling', icon: 'sync' },
  // dual_reporting is RETIRED per the direct-cutover ruling (Plan 003 §4d,
  // Toby 2026-07-16) — no badge treatment shipped for it. Any row a stale
  // seed/fixture still carries with this value falls back to the icon-less
  // default arm below rather than rendering a fifth removed state.
};

/** @param {string} status one of AgentStatus's live values (dual_reporting excluded) */
export function agentStatusMeta(status) {
  return AGENT_STATUS_META[status] || { label: titleCase(status) || 'Unknown', icon: 'question' };
}

/** Sub-line under the agent badge — version for healthy, staleness/enrolment context otherwise. */
export function agentStatusSub(server) {
  const status = server.agent_status;
  if (status === 'healthy') {
    return server.luna_version ? `v${server.luna_version}` : null;
  }
  if (status === 'stale') {
    return server.last_heartbeat_at ? `last seen ${formatRelativeTime(server.last_heartbeat_at)}` : 'last seen unknown';
  }
  if (status === 'unmanaged') {
    return 'detected via provider API';
  }
  if (status === 'enrolling') {
    return 'check-in pending';
  }
  return null;
}

/** health -> the fleet table's dot / status-pill colour class, honouring the "enrolling gets its own pending dot" rule. */
export function healthDotClass(server) {
  if (server.agent_status === 'enrolling') {
    return 'h-pending';
  }
  switch (server.health) {
    case 'ok':
      return 'h-ok';
    case 'warning':
      return 'h-warn';
    case 'critical':
      return 'h-crit';
    default:
      return 'h-unk';
  }
}

export function healthLabel(health) {
  switch (health) {
    case 'ok':
      return 'OK';
    case 'warning':
      return 'Warning';
    case 'critical':
      return 'Critical';
    default:
      return 'Unknown';
  }
}

export function formatRelativeTime(epochSeconds) {
  if (!epochSeconds) {
    return null;
  }
  const deltaSeconds = Math.max(0, Math.floor(Date.now() / 1000) - epochSeconds);
  if (deltaSeconds < 60) {
    return `${deltaSeconds}s ago`;
  }
  if (deltaSeconds < 3600) {
    return `${Math.floor(deltaSeconds / 60)}m ago`;
  }
  if (deltaSeconds < 86400) {
    return `${Math.floor(deltaSeconds / 3600)}h ago`;
  }
  return `${Math.floor(deltaSeconds / 86400)}d ago`;
}

export function formatEpoch(epochSeconds) {
  if (!epochSeconds) {
    return null;
  }
  return new Date(epochSeconds * 1000).toLocaleString();
}

/** Percentage-threshold meter colour (disk/mem/uptime-style "higher is worse" metrics). */
export function meterToneForPct(pct) {
  if (pct === null || pct === undefined) {
    return null;
  }
  if (pct >= 90) {
    return 'm-crit';
  }
  if (pct >= 80) {
    return 'm-warn';
  }
  return 'm-ok';
}

/**
 * Unix load-average -> a 0-100 visual bar width. NOT a percentage of
 * anything real (a load average of 1.0 means "one core's worth of queued
 * work", not "100% of some resource") — scaled against a configurable
 * per-core reference so the bar stays a rough-magnitude indicator, never a
 * relabelled unit. The raw figure (not this scaled width) is always what's
 * printed as text.
 */
export function loadBarWidthPct(load, reference = 4) {
  if (load === null || load === undefined) {
    return 0;
  }
  return Math.max(2, Math.min(100, (load / reference) * 100));
}

/**
 * Cert-expiry column (Plan 011 §5.2) — `server.agent_cert_not_after` is
 * epoch seconds or null (every row, until the cert-reporting agent ships).
 * Amber < 30 days, red < 7, grey "unknown" — matches the plan's UI bullet
 * exactly.
 */
export function certExpiryDaysRemaining(server) {
  const notAfter = server?.agent_cert_not_after;
  if (!notAfter) {
    return null;
  }
  return Math.floor((notAfter - Math.floor(Date.now() / 1000)) / 86400);
}

export function certExpiryTone(server) {
  const days = certExpiryDaysRemaining(server);
  if (days === null) {
    return 'cert-unknown';
  }
  if (days < 7) {
    return 'cert-crit';
  }
  if (days < 30) {
    return 'cert-warn';
  }
  return 'cert-ok';
}

export function certExpiryLabel(server) {
  const days = certExpiryDaysRemaining(server);
  if (days === null) {
    return 'Unknown';
  }
  if (days < 0) {
    return `Expired ${Math.abs(days)}d ago`;
  }
  // Under a day left rounds to "0d", which reads as already-expired — show
  // hours instead so the last day before expiry isn't indistinguishable
  // from "just expired". Tone (certExpiryTone) is unchanged: still whatever
  // the day-based thresholds say for `days < 1` (i.e. still `cert-crit`).
  if (days < 1) {
    const notAfter = server?.agent_cert_not_after;
    const hours = Math.max(0, Math.floor((notAfter - Math.floor(Date.now() / 1000)) / 3600));
    return `${hours}h`;
  }
  return `${days}d`;
}

export function loadBarTone(load, reference = 4) {
  if (load === null || load === undefined) {
    return null;
  }
  const ratio = load / reference;
  if (ratio >= 0.9) {
    return 'm-crit';
  }
  if (ratio >= 0.8) {
    return 'm-warn';
  }
  return 'm-ok';
}
